import { BOOST_BARS, callingIdSchema, COOLDOWN_GROUPS, COUNTERS, disciplineSchema, FIRST_STEP_KEYS, qualitySchema, skillIdSchema, statSchema, townIdSchema } from '@wyn/content';
import { z } from 'zod';
import { joustingMoveSchemas, joustingViewSchema } from './minigames/jousting.ts';
import { huntingMoveSchemas, huntingViewSchema } from './minigames/hunting.ts';
import { siegeMoveSchemas, siegeViewSchema } from './minigames/siege.ts';
import { cardsMoveSchemas, cardsViewSchema } from './minigames/cards.ts';
import { alchemyMoveSchemas, alchemyViewSchema } from './minigames/alchemy.ts';
import { hagglingMoveSchemas, hagglingViewSchema } from './minigames/haggling.ts';
import { fightReportSchema, loadoutSchema } from './fight-report.ts';

/** Client-generated id that makes every mutating request idempotent. */
export const actionIdSchema = z.uuid();

export const errorResponseSchema = z.object({
  error: z.object({ code: z.string(), message: z.string() }),
});
export type ErrorResponse = z.infer<typeof errorResponseSchema>;

export const barViewSchema = z.object({
  value: z.number(),
  max: z.number(),
  nextAt: z.number().nullable(),
  fullAt: z.number().nullable(),
});

export const statusSchema = z.discriminatedUnion('kind', [
  z.object({ kind: z.literal('idle') }),
  z.object({ kind: z.literal('infirmary'), endsAt: z.number(), reason: z.string() }),
  z.object({ kind: z.literal('gaol'), endsAt: z.number(), reason: z.string() }),
  z.object({ kind: z.literal('travelling'), from: townIdSchema, to: townIdSchema, departedAt: z.number(), endsAt: z.number() }),
]);

/** A steward's mute or ban in force. `until` null is permanent. */
export const sanctionSchema = z.object({ kind: z.enum(['mute', 'ban']), until: z.number().nullable(), reason: z.string(), at: z.number() });
export type SanctionView = z.infer<typeof sanctionSchema>;

/** A birth month, `YYYY-MM`. The age rules are game-core's (`assertOldEnough`). */
export const bornSchema = z.string().regex(/^\d{4}-(0[1-9]|1[0-2])$/, 'Choose the month and year you were born.');

export const playerSnapshotSchema = z.object({
  id: z.string(),
  name: z.string(),
  calling: callingIdSchema,
  homeTown: townIdSchema,
  town: townIdSchema,
  level: z.number().int(),
  renown: z.object({ total: z.number(), into: z.number(), toNext: z.number().nullable() }),
  bars: z.object({ life: barViewSchema, vigour: barViewSchema, nerve: barViewSchema, focus: barViewSchema, mood: barViewSchema }),
  stats: z.object({ strength: z.number(), defence: z.number(), speed: z.number(), dexterity: z.number() }),
  purse: z.number().int(),
  bank: z.number().int(),
  /** Coin locked in the Royal Bank for a term. */
  locked: z.number().int(),
  status: statusSchema,
  /** Coin to walk out of gaol now; null when not in gaol. */
  bail: z.number().int().nullable(),
  /** The minigame in progress; null when none (or it has gone cold). */
  minigame: z.object({ sessionId: z.string(), venue: z.string(), activity: z.string(), startedAt: z.number(), expiresAt: z.number() }).nullable(),
  /** Worn and wielded gear. */
  loadout: loadoutSchema,
  /** The fight this player started and has not settled; null when none (or it has gone cold). */
  fight: z.object({ fightId: z.string(), defenderId: z.string(), defenderName: z.string(), startedAt: z.number(), expiresAt: z.number() }).nullable(),
  /** Nobody may attack you while this is set: under level 5, or until the time given. */
  protection: z.object({ level: z.boolean(), until: z.number() }).nullable(),
  /** Mastery tier reached in each Discipline (left out at 0). */
  mastery: z.partialRecord(disciplineSchema, z.number().int().min(1).max(4)),
  /** The title shown beside the name: the one chosen, or else the highest mastery title; null before the first. */
  title: z.string().nullable(),
  /** Every title earned, from mastery (`mastery:<discipline>:<tier>`) and deeds (`deed:<key>`), and the one chosen (null: the default). */
  titles: z.array(z.object({ id: z.string(), title: z.string() })),
  titleChoice: z.string().nullable(),
  /** What the player has done: lifetime counts, plays and best score per activity (`venue/activity`), feats (`engine.feat`), towns visited, and when each deed was done. */
  record: z.object({
    counts: z.partialRecord(z.enum(COUNTERS), z.number()),
    games: z.record(z.string(), z.object({ plays: z.number().int(), best: z.number() })),
    feats: z.record(z.string(), z.number().int()),
    towns: z.array(townIdSchema),
    deeds: z.record(z.string(), z.number()),
  }),
  /** The first-steps chain: when each step was done (by key), and when it was put aside. Null for a player not on it (older players, residents). */
  firstSteps: z.object({ done: z.partialRecord(z.enum(FIRST_STEP_KEYS), z.number()), dismissed: z.number().nullable() }).nullable(),
  /** The day's notice board, as of the server's now. Null for a resident. */
  board: z
    .object({
      day: z.string(),
      /** The next UTC midnight, when a new three are drawn. */
      endsAt: z.number(),
      tasks: z.array(
        z.object({
          key: z.string(),
          bar: z.enum(['focus', 'vigour', 'nerve']),
          name: z.string(),
          hint: z.string(),
          unit: z.enum(['focus', 'vigour', 'nerve', 'wins']),
          target: z.number(),
          progress: z.number(),
          done: z.boolean(),
          venue: z.string().nullable(),
        }),
      ),
      done: z.boolean(),
      streak: z.number(),
      reward: z.number(),
      bonus: z.number(),
    })
    .nullable(),
  /** When each consumable group comes off cooldown; only groups still cooling down. */
  cooldowns: z.partialRecord(z.enum(COOLDOWN_GROUPS), z.number()),
  /** When the Herald will next change your Calling (0: any time), and the fee. */
  callingChange: z.object({ at: z.number(), fee: z.number().int() }),
  /** A mute or ban in force; null when none. */
  sanction: sanctionSchema.nullable(),
  /** A steward has taken the name away (why, and when): nothing else can be done until a new one is chosen. */
  renameRequired: z.object({ reason: z.string(), at: z.number() }).nullable(),
  /** Off: the Talk page is empty and no chat is pushed. Set with `POST /v1/player/chat`. */
  chat: z.enum(['on', 'off']),
  /** Birth month (`YYYY-MM`); null for a player who signed up before it was asked. */
  born: z.string().nullable(),
  /** Whether this player's play goes to analytics now: their choice, or the default for their age. */
  analytics: z.enum(['on', 'off']),
  /** True once the player has chosen; false while the default for their age applies. */
  analyticsChosen: z.boolean(),
  version: z.number().int(),
  serverTime: z.number(),
});
export type PlayerSnapshot = z.infer<typeof playerSnapshotSchema>;

export const skillViewSchema = z.object({
  skill: skillIdSchema,
  level: z.number().int(),
  xp: z.number(),
  into: z.number(),
  toNext: z.number().nullable(),
});

export const inventoryLineSchema = z.object({
  itemKey: z.string(),
  quality: qualitySchema,
  quantity: z.number().int(),
});

// GET /v1/player/me
/** A player someone has blocked, under the name they had then. */
export const blockViewSchema = z.object({ id: z.string(), name: z.string(), at: z.number() });
export type BlockView = z.infer<typeof blockViewSchema>;

export const meResponseSchema = z.object({
  player: playerSnapshotSchema.nullable(),
  skills: z.array(skillViewSchema),
  inventory: z.array(inventoryLineSchema),
  /** The version of the rules and terms this account last agreed to; null when never (or before there was one). */
  termsVersion: z.string().nullable(),
  /** A steward (the `admins` group): the game links to /admin. */
  isAdmin: z.boolean(),
  /** Players this one has blocked, oldest first: the client hides their pushed lines too. */
  blocks: z.array(blockViewSchema),
});
export type MeResponse = z.infer<typeof meResponseSchema>;

// POST /v1/player/character
export const createCharacterRequestSchema = z.object({
  actionId: actionIdSchema,
  name: z.string().min(1).max(40),
  calling: callingIdSchema,
  homeTown: townIdSchema,
  /** The `TERMS_VERSION` the player agreed to the rules and terms against. */
  termsVersion: z.string().max(20),
  born: bornSchema,
});
export type CreateCharacterRequest = z.infer<typeof createCharacterRequestSchema>;
export const createCharacterResponseSchema = z.object({ player: playerSnapshotSchema });
export type CreateCharacterResponse = z.infer<typeof createCharacterResponseSchema>;

// POST /v1/player/terms — agree again after the rules or terms change. Setting it twice is harmless.
// A player who signed up before birth months were asked gives theirs here too.
export const acceptTermsRequestSchema = z.object({ termsVersion: z.string().max(20), born: bornSchema.optional() });
export type AcceptTermsRequest = z.infer<typeof acceptTermsRequestSchema>;
export const acceptTermsResponseSchema = z.object({ termsVersion: z.string() });

// POST /v1/player/name — choose a new name after a steward has taken the old one away.
export const chooseNameRequestSchema = z.object({ actionId: actionIdSchema, name: z.string().min(1).max(40) });
export const chooseNameResponseSchema = z.object({ player: playerSnapshotSchema });

// POST /v1/player/chat — turn chat on or off.
export const setChatRequestSchema = z.object({ actionId: actionIdSchema, chat: z.enum(['on', 'off']) });
export const setChatResponseSchema = z.object({ player: playerSnapshotSchema });

// POST /v1/player/analytics — let play go to analytics, or not.
export const setAnalyticsRequestSchema = z.object({ actionId: actionIdSchema, analytics: z.enum(['on', 'off']) });
export const setAnalyticsResponseSchema = z.object({ player: playerSnapshotSchema });

// POST /v1/player/title — go by an earned title (`titles` on the snapshot), or by the default (null).
export const setTitleRequestSchema = z.object({ actionId: actionIdSchema, title: z.string().max(100).nullable() });
export const setTitleResponseSchema = z.object({ player: playerSnapshotSchema });

// POST /v1/player/first-steps/dismiss — put the first-steps chain aside: no panel, and nothing more counted or paid.
export const dismissFirstStepsRequestSchema = z.object({ actionId: actionIdSchema });
export const dismissFirstStepsResponseSchema = z.object({ player: playerSnapshotSchema });

// GET /v1/player/deeds — the Deeds page. Hidden deeds not yet done are only counted.
export const deedViewSchema = z.object({
  key: z.string(),
  name: z.string(),
  flavour: z.string(),
  /** What to do, said plainly. */
  hint: z.string(),
  hidden: z.boolean(),
  /** When it was done; null if not yet. */
  doneAt: z.number().nullable(),
  progress: z.object({ have: z.number(), need: z.number() }),
  /** What it pays, in words; empty for nothing but the telling. */
  reward: z.string(),
});
export type DeedView = z.infer<typeof deedViewSchema>;
export const deedsResponseSchema = z.object({
  /** Done (newest first), then visible deeds still to do (closest first). */
  deeds: z.array(deedViewSchema),
  done: z.number().int(),
  total: z.number().int(),
  hiddenLeft: z.number().int(),
});
export type DeedsResponse = z.infer<typeof deedsResponseSchema>;

// POST /v1/player/train
export const trainRequestSchema = z.object({
  actionId: actionIdSchema,
  stat: statSchema,
  vigour: z.number().int().min(1).max(1000),
});
export type TrainRequest = z.infer<typeof trainRequestSchema>;
export const trainResponseSchema = z.object({
  player: playerSnapshotSchema,
  outcome: z.object({ stat: statSchema, vigourSpent: z.number(), moodSpent: z.number(), gain: z.number(), newValue: z.number(), renown: z.number() }),
});
export type TrainResponse = z.infer<typeof trainResponseSchema>;

// GET /v1/health
export const healthResponseSchema = z.object({ ok: z.literal(true), stage: z.string(), time: z.number() });

const actionResponse = <O extends z.ZodType>(outcome: O) => z.object({ player: playerSnapshotSchema, outcome });

// POST /v1/player/travel
export const travelRequestSchema = z.object({ actionId: actionIdSchema, to: townIdSchema });
export type TravelRequest = z.infer<typeof travelRequestSchema>;
export const travelResponseSchema = actionResponse(z.object({ from: townIdSchema, to: townIdSchema, endsAt: z.number() }));

// POST /v1/player/crime
export const crimeRequestSchema = z.object({ actionId: actionIdSchema, crime: z.string().min(1).max(40) });
export type CrimeRequest = z.infer<typeof crimeRequestSchema>;
export const crimeOutcomeSchema = z.object({
  crime: z.string(),
  result: z.enum(['success', 'failure', 'caught', 'mishap']),
  text: z.string(),
  nerveSpent: z.number().int(),
  coin: z.number().int(),
  renown: z.number().int(),
  lifeLost: z.number(),
  endsAt: z.number().nullable(),
});
export type CrimeOutcome = z.infer<typeof crimeOutcomeSchema>;
export const crimeResponseSchema = actionResponse(crimeOutcomeSchema);

// POST /v1/player/bail
export const bailRequestSchema = z.object({ actionId: actionIdSchema });
export type BailRequest = z.infer<typeof bailRequestSchema>;
export const bailResponseSchema = actionResponse(z.object({ paid: z.number().int() }));

// POST /v1/player/use
export const useItemRequestSchema = z.object({ actionId: actionIdSchema, itemKey: z.string().min(1).max(60), quality: qualitySchema });
export type UseItemRequest = z.infer<typeof useItemRequestSchema>;
export const useItemResponseSchema = actionResponse(
  z.object({
    itemKey: z.string(),
    lifeRestored: z.number(),
    minutesOff: z.number().int(),
    discharged: z.boolean(),
    gains: z.partialRecord(z.enum(BOOST_BARS), z.number()),
    cooldownUntil: z.number().nullable(),
  }),
);

// POST /v1/player/calling — the Herald's Office in Wynchester.
export const changeCallingRequestSchema = z.object({ actionId: actionIdSchema, calling: callingIdSchema });
export type ChangeCallingRequest = z.infer<typeof changeCallingRequestSchema>;
export const changeCallingResponseSchema = actionResponse(z.object({ from: callingIdSchema, to: callingIdSchema, fee: z.number().int(), nextAt: z.number() }));

// POST /v1/player/buy
export const buyRequestSchema = z.object({
  actionId: actionIdSchema,
  shop: z.string().min(1).max(40),
  itemKey: z.string().min(1).max(60),
  quantity: z.number().int().min(1).max(10),
});
export type BuyRequest = z.infer<typeof buyRequestSchema>;
export const buyResponseSchema = actionResponse(z.object({ shop: z.string(), itemKey: z.string(), quantity: z.number().int(), cost: z.number().int() }));

// GET /v1/player/events?before=&limit=
export const eventsRequestSchema = z.object({
  before: z.string().max(80).optional(),
  limit: z.coerce.number().int().min(1).max(50).optional(),
});
export type EventsRequest = z.input<typeof eventsRequestSchema>;
export const eventsResponseSchema = z.object({
  events: z.array(
    z.object({
      id: z.string(),
      type: z.string(),
      text: z.string(),
      at: z.number(),
      /** A fight's turn-by-turn report (type `fight`). */
      report: fightReportSchema.optional(),
    }),
  ),
  next: z.string().nullable(),
});
export type EventsResponse = z.infer<typeof eventsResponseSchema>;

// GET /v1/player/town
/** The most Folk about town returned at once. A busier town is searched by name. */
export const FOLK_LIMIT = 100;
export const townRequestSchema = z.object({
  /** Only those whose name contains this, ignoring case. */
  name: z.string().trim().max(40).optional(),
});
export type TownRequest = z.input<typeof townRequestSchema>;
export const townResponseSchema = z.object({
  town: townIdSchema,
  /** Newest arrivals first, at most `FOLK_LIMIT`, leaving out the player asking and anyone they have blocked. `resident`: a simulated player the server runs while the realm is young. */
  people: z.array(z.object({ id: z.string(), name: z.string(), resident: z.boolean().optional() })),
  /** How many matched before the limit: more than `people.length` when some were left out. */
  total: z.number().int(),
  /** When the next traveller already on the road here arrives; the list changes then without a write to push. */
  nextArrivalAt: z.number().nullable(),
});
export type TownResponse = z.infer<typeof townResponseSchema>;

// POST /v1/dev/clock — local docker compose only: moves the server clock forward.
export const devClockRequestSchema = z.object({ advanceMs: z.number().int().min(0).max(7 * 24 * 60 * 60 * 1000) });
export const devClockResponseSchema = z.object({ time: z.number(), offsetMs: z.number() });
// POST /v1/dev/sanction — local docker compose only: mute, ban or lift a player by name, without a steward's sign-in.
export const devSanctionRequestSchema = z.object({
  name: z.string().min(1).max(40),
  kind: z.enum(['mute', 'ban', 'lift']),
  /** How long; omitted or null for good. Ignored when lifting. */
  minutes: z.number().int().min(1).max(60 * 24 * 365).nullable().optional(),
  reason: z.string().max(200).default('Testing.'),
});
export const devSanctionResponseSchema = z.object({ sanction: sanctionSchema.nullable() });
// POST /v1/dev/grant — local docker compose only: Renown for the caller, and an age, to test fights against grown players.
export const devGrantRequestSchema = z.object({
  renown: z.number().int().min(0).max(1_000_000),
  ageHours: z.number().int().min(0).max(24 * 365).optional(),
  /** Raise these skills to at least these levels. */
  skills: z.partialRecord(skillIdSchema, z.number().int().min(1).max(100)).optional(),
});
export const devGrantResponseSchema = z.object({ renown: z.number(), createdAt: z.number() });

// ---- Minigames (/v1/minigame/*) ----

const pair = z.tuple([z.number().int().min(1).max(6), z.number().int().min(1).max(6)]);
const rock = z.enum(['rock', 'ore', 'unstable']);

export const miningViewSchema = z.object({
  width: z.number().int(),
  height: z.number().int(),
  cells: z.array(z.union([z.null(), z.object({ kind: z.literal('ore') }), z.object({ kind: z.literal('unstable') }), z.object({ kind: z.literal('rock'), ore: z.number().int(), cracked: z.boolean() })])),
  swings: z.number().int(),
  found: z.number().int(),
  ore: z.number().int(),
  collapses: z.number().int(),
  maxCollapses: z.number().int(),
  over: z.boolean(),
  revealed: z.array(rock).nullable(),
});
export type MiningView = z.infer<typeof miningViewSchema>;

export const diceViewSchema = z.object({
  me: z.number().int(),
  house: z.number().int(),
  pot: z.number().int(),
  goal: z.number().int(),
  houseGoal: z.number().int(),
  palms: z.number().int(),
  palmRisk: z.number(),
  awaiting: z.enum(['roll', 'palm', 'ride', 'over']),
  last: pair.nullable(),
  houseRolls: z.array(pair),
  say: z.string(),
  won: z.boolean().nullable(),
  /** Times the winnings were let ride. */
  rides: z.number().int(),
  /** What a win pays now. */
  winnings: z.number().int(),
});
export type DiceView = z.infer<typeof diceViewSchema>;

const sessionBase = {
  id: z.string(),
  venue: z.string(),
  activity: z.string(),
  level: z.number().int(),
  /** SHA-256 of the server seed, shown before anything is rolled or dug. */
  commitment: z.string(),
  step: z.number().int(),
  startedAt: z.number(),
  expiresAt: z.number(),
  /** The good on the table, for venues that trade. */
  ware: z.object({ itemKey: z.string(), quality: qualitySchema, value: z.number() }).nullable(),
};
const turn = <E extends string, V extends z.ZodType>(engine: E, view: V) => z.object({ ...sessionBase, engine: z.literal(engine), seed: z.null(), view });
const live = <E extends string>(engine: E) => z.object({ ...sessionBase, engine: z.literal(engine), seed: z.string(), view: z.null() });

/** Turn-based games carry their public view; real-time games carry the seed the client simulates from. */
export const sessionViewSchema = z.discriminatedUnion('engine', [
  turn('mining', miningViewSchema),
  turn('dice', diceViewSchema),
  turn('jousting', joustingViewSchema),
  turn('hunting', huntingViewSchema),
  turn('siege', siegeViewSchema),
  turn('cards', cardsViewSchema),
  turn('alchemy', alchemyViewSchema),
  turn('haggling', hagglingViewSchema),
  live('woodcutting'),
  live('fishing'),
  live('smithing'),
  live('brewing'),
  live('horsemanship'),
  live('lockpicking'),
  live('archery'),
  live('illumination'),
]);
export type SessionView = z.infer<typeof sessionViewSchema>;

export const minigameMoveSchema = z.discriminatedUnion('type', [
  z.object({ type: z.literal('swing'), cell: z.number().int().min(0).max(63) }),
  z.object({ type: z.literal('leave') }),
  z.object({ type: z.literal('roll') }),
  z.object({ type: z.literal('bank') }),
  z.object({ type: z.literal('palm') }),
  z.object({ type: z.literal('pass') }),
  z.object({ type: z.literal('ride') }),
  z.object({ type: z.literal('collect') }),
  ...joustingMoveSchemas,
  ...huntingMoveSchemas,
  ...siegeMoveSchemas,
  ...cardsMoveSchemas,
  ...alchemyMoveSchemas,
  ...hagglingMoveSchemas,
]);
export type MinigameMove = z.infer<typeof minigameMoveSchema>;

export const settlementSchema = z.object({
  sessionId: z.string(),
  venue: z.string(),
  activity: z.string(),
  skill: skillIdSchema,
  score: z.number(),
  title: z.string(),
  text: z.string(),
  breakdown: z.array(z.tuple([z.string(), z.number()])),
  xp: z.number().int(),
  levelBefore: z.number().int(),
  level: z.number().int(),
  renown: z.number().int(),
  yield: z.object({ itemKey: z.string(), quality: qualitySchema, quantity: z.number().int() }).nullable(),
  coin: z.number().int(),
  /** Feats done in the attempt, by the engine's names (`FEATS` in game-core names them). */
  feats: z.array(z.string()),
  /** The best score at this activity before this attempt; null for the first on record. */
  best: z.number().nullable(),
  /** Turn-based games reveal the seed and moves so the client can check them against the commitment. */
  reveal: z
    .object({ serverSeed: z.string(), clientSeed: z.string().nullable(), moves: z.array(z.object({ move: minigameMoveSchema, elapsedMs: z.number() })) })
    .nullable(),
});
export type Settlement = z.infer<typeof settlementSchema>;

// POST /v1/minigame/start
export const minigameStartRequestSchema = z.object({
  actionId: actionIdSchema,
  venue: z.string().min(1).max(40),
  activity: z.string().min(1).max(40),
  /** The good brought to the table, for venues that trade (the Market Cross). */
  ware: z.object({ itemKey: z.string().min(1).max(60), quality: qualitySchema }).optional(),
});
export type MinigameStartRequest = z.infer<typeof minigameStartRequestSchema>;
export const minigameStartResponseSchema = z.object({ player: playerSnapshotSchema, session: sessionViewSchema });

// POST /v1/minigame/move
export const minigameMoveRequestSchema = z.object({
  actionId: actionIdSchema,
  sessionId: z.string().min(1).max(40),
  move: minigameMoveSchema,
  /** Mixed into the dice RNG on the first move, after the server has committed to its seed. */
  clientSeed: z.string().min(1).max(64).optional(),
});
export type MinigameMoveRequest = z.infer<typeof minigameMoveRequestSchema>;
export const minigameMoveResponseSchema = z.object({
  session: sessionViewSchema,
  /** Set on the move that ends the game, with the player after payout. */
  settlement: settlementSchema.nullable(),
  player: playerSnapshotSchema.nullable(),
});
export type MinigameMoveResponse = z.infer<typeof minigameMoveResponseSchema>;

// POST /v1/minigame/finish — a real-time game's input trace, replayed and scored by the server.
export const inputTraceSchema = z.object({
  ticks: z.number().int().min(0).max(60 * 600),
  changes: z
    .array(
      z.union([
        z.tuple([z.number().int().min(0), z.number().int().min(0).max(15)]),
        z.tuple([z.number().int().min(0), z.number().int().min(0).max(15), z.number().int().min(0).max(1000), z.number().int().min(0).max(1000)]),
      ]),
    )
    .max(12000),
});
export const minigameFinishRequestSchema = z.object({ actionId: actionIdSchema, sessionId: z.string().min(1).max(40), trace: inputTraceSchema });
export type MinigameFinishRequest = z.infer<typeof minigameFinishRequestSchema>;
export const minigameFinishResponseSchema = z.object({ player: playerSnapshotSchema, settlement: settlementSchema });
export type MinigameFinishResponse = z.infer<typeof minigameFinishResponseSchema>;

// POST /v1/minigame/abandon
export const minigameAbandonRequestSchema = z.object({ actionId: actionIdSchema });
// POST /v1/minigame/craft — a cross-skill recipe, made at its venue.
export const craftRequestSchema = z.object({ actionId: actionIdSchema, recipe: z.string().min(1).max(60) });
export type CraftRequest = z.infer<typeof craftRequestSchema>;
export const craftResponseSchema = actionResponse(
  z.object({
    recipe: z.string(),
    product: z.object({ itemKey: z.string(), quality: qualitySchema, quantity: z.number().int() }),
    spared: z.boolean(),
    xp: z.partialRecord(skillIdSchema, z.number()),
    renown: z.number(),
  }),
);

export const minigameAbandonResponseSchema = z.object({ player: playerSnapshotSchema, outcome: z.object({ sessionId: z.string() }) });

// GET /v1/minigame/session — the game in progress, to resume after a reload.
export const minigameSessionResponseSchema = z.object({ session: sessionViewSchema.nullable() });

/** Payloads EventBridge Scheduler delivers to the jobs Lambda. */
export const jobSchema = z.discriminatedUnion('kind', [
  z.object({ kind: z.literal('timer-ended'), playerId: z.string(), status: z.enum(['travelling', 'gaol', 'infirmary']), endsAt: z.number() }),
  z.object({ kind: z.literal('rollover') }),
  // By hand (aws lambda invoke), for a stage that had players before the leaderboards existed.
  z.object({ kind: z.literal('boards-rebuild') }),
  // Four times a day: builds the players files it can and starts the day's exports for Athena.
  z.object({ kind: z.literal('snapshot') }),
  // By hand: a full export as today began, which also starts the players chain again. Once a day.
  z.object({ kind: z.literal('export') }),
  // By hand: every player's balances against their ledger. Drift is logged at ERROR and fails the job.
  z.object({ kind: z.literal('reconcile') }),
]);
export type Job = z.infer<typeof jobSchema>;
